/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToOrderTemplateComponent } from '@msdyn365-commerce/components';
import { getCatalogId } from '@msdyn365-commerce/core';
import { ProductSearchCriteria, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    getEstimatedAvailabilityAsync,
    searchByCriteriaAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { createInventoryAvailabilitySearchCriteria } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { getSimpleProductsAction, ProductInput } from '../../actions/get-simple-products.override.action';
import { SupersessionKey } from '../../themes/gardner-theme/models/SupersessionKey';
import getExtensionPropertyValueByKey, { CommercePropertyValueEnum } from '../../utils/getExtensionPropertyValueByKey';
import { IPromoModalProps, PromoModal } from './components/promo-modal.component';
import { IGdnPromoProgramData } from './gdn-promo-program.data';
import { IGdnPromoProgramProps } from './gdn-promo-program.props.autogenerated';

/**
 * The only supported token format
 *
 *   {quantity:ITEM}   {add_to_cart:ITEM}   {add_to_template:ITEM}
 *
 */
const TOKEN_PATTERN = /(?<!\{)\{(quantity|add_to_cart|add_to_template):([^{}]+)\}(?!\})/g;

/**
 * Stamped by the module onto every control it generates, never written by Gardner. Once a
 * token is replaced by a button the token text is gone, so this is how a click finds its item.
 */
const ITEM_NUMBER_ATTRIBUTE = 'data-item-number';

/** Classes on the generated controls. They match the existing promo stylesheet. */
const QUANTITY_CLASS = 'quantity-input';
const ADD_TO_CART_CLASS = 'add-to-cart-btn';
const ADD_TO_TEMPLATE_CLASS = 'add-to-order-template-btn';
const LOADING_CLASS = 'gdn-promo-program__action--loading';

/** Inventory level code Commerce returns for stock that could only be back ordered. */
const BACK_ORDER_LEVEL_CODE = 'BO';

type PromoAction = 'addToCart' | 'addToTemplate';

export interface IGdnPromoProgramViewProps extends IGdnPromoProgramProps<IGdnPromoProgramData> {
    moduleProps: IModuleProps;
    promoContentProps: React.HTMLAttributes<HTMLDivElement>;
    addToOrderTemplate: React.ReactNode;
    modal: React.ReactNode;
}

/** A Commerce action the customer asked for, before the item has been resolved. */
interface IPromoActionRequest {
    action: PromoAction;
    itemNumber: string;
    quantity: number;
    button: HTMLElement;
}

/** The same action once the item number has been resolved to a product. */
interface IResolvedPromoAction extends IPromoActionRequest {
    product: SimpleProduct;
}

/** The product and quantity handed to the Commerce order template dialog. */
interface IOrderTemplateRequest {
    product: SimpleProduct;
    quantity: number;

    /** Increments per click, so the dialog reopens even for the same item twice in a row. */
    id: number;
}

type PromoModalContent = Omit<IPromoModalProps, 'isOpen' | 'onClose'>;

interface IGdnPromoProgramState {
    modal?: PromoModalContent;
    orderTemplateRequest?: IOrderTemplateRequest;
}

/**
 * Renders Gardner-authored promotional HTML and wires the Commerce actions inside it.
 *
 * The module owns the behaviour, not the layout: Gardner controls the markup and only places
 * tokens where the Commerce controls should appear.
 */
class GdnPromoProgram extends React.PureComponent<IGdnPromoProgramProps<IGdnPromoProgramData>, IGdnPromoProgramState> {
    public state: IGdnPromoProgramState = {};

    private readonly _contentRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

    private readonly _orderTemplateHostRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

    /**
     * Resolved products keyed by item number. A null entry means the item was looked up and
     * does not resolve, so repeated clicks do not re-issue a request that is known to fail.
     */
    private readonly _productCache: Map<string, SimpleProduct | null> = new Map<string, SimpleProduct | null>();

    /**
     * Inventory level code per resolved product, populated only on sites that disable back
     * orders. An absent entry means availability was never checked.
     */
    private readonly _availabilityByRecordId: Map<number, string> = new Map<number, string>();

    /** Item numbers with an action in flight, so repeated clicks are ignored. */
    private readonly _pendingItems: Set<string> = new Set<string>();

    /** The promo content the memoised markup below was built from. */
    private _processedSource?: string;

    /** Promo content with its tokens replaced, memoised so re-renders are free. */
    private _processedHtml: string = '';

    /** Every item number found in the tokens, collected while the content is processed. */
    private _tokenItemNumbers: string[] = [];

    /** The in-flight background lookup of every item on the page, if one was started. */
    private _prefetch?: Promise<void>;

    /**
     * The action waiting on the customer to dismiss a supersession notice. Held outside state
     * because it carries the clicked DOM element, which React never renders.
     */
    private _pendingAction?: IResolvedPromoAction;

    private _orderTemplateRequestCount: number = 0;

    private _openedOrderTemplateRequestId: number = 0;

    public componentDidMount(): void {
        this._prefetchProducts();
    }

    public componentDidUpdate(): void {
        this._openOrderTemplateDialogIfRequested();
    }

    public render(): JSX.Element | null {
        const { config } = this.props;
        const { modal } = this.state;
        const baseClassName = 'gdn-promo-program';

        const viewProps: IGdnPromoProgramViewProps = {
            ...this.props,
            moduleProps: {
                moduleProps: this.props,
                className: classnames(baseClassName, config.className)
            },
            promoContentProps: {
                className: `${baseClassName}__content`,
                ref: this._contentRef,
                onClick: this._handlePromoClick,
                // eslint-disable-next-line @typescript-eslint/naming-convention -- React API.
                dangerouslySetInnerHTML: { __html: this._getPromoHtml() }
            } as React.HTMLAttributes<HTMLDivElement>,
            addToOrderTemplate: this._renderAddToOrderTemplate(),
            modal: (
                <PromoModal
                    isOpen={Boolean(modal)}
                    heading={modal?.heading ?? ''}
                    message={modal?.message ?? ''}
                    detail={modal?.detail}
                    okButtonLabel={modal?.okButtonLabel ?? ''}
                    className={modal?.className}
                    onClose={this._closeModal}
                />
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Returns the promo content with its tokens replaced, rebuilding it only when the authored
     * content changes so the rendered markup keeps a stable value between renders.
     * @returns The promo markup to render.
     */
    private _getPromoHtml(): string {
        const source = (this.props.config.promoContent as string | undefined) || '';

        if (source !== this._processedSource) {
            const itemNumbers = new Set<string>();

            this._processedHtml = source.replace(TOKEN_PATTERN, (match: string, token: string, rawItemNumber: string) => {
                const itemNumber = rawItemNumber.trim();

                if (!itemNumber) {
                    return match;
                }

                itemNumbers.add(itemNumber);

                switch (token) {
                    case 'quantity':
                        return this._buildQuantityInput(itemNumber);
                    case 'add_to_cart':
                        return this._buildButton(ADD_TO_CART_CLASS, itemNumber, this.props.resources.addToCartButtonText);
                    default:
                        return this._buildButton(ADD_TO_TEMPLATE_CLASS, itemNumber, this.props.resources.addToOrderTemplateButtonText);
                }
            });

            this._processedSource = source;
            this._tokenItemNumbers = Array.from(itemNumbers);
        }

        return this._processedHtml;
    }

    /**
     * @param itemNumber - The item number from the token.
     * @returns The quantity input markup for a quantity token.
     */
    private _buildQuantityInput(itemNumber: string): string {
        const item = this._escapeHtml(itemNumber);
        const ariaLabel = this._escapeHtml(this.props.resources.quantityAriaLabelFormat.replace('{0}', itemNumber));

        return (
            `<input type="number" class="${QUANTITY_CLASS}" inputmode="numeric" min="1" step="1" value="1"` +
            ` aria-label="${ariaLabel}" ${ITEM_NUMBER_ATTRIBUTE}="${item}" />`
        );
    }

    /**
     * @param className - The class identifying which action the button performs.
     * @param itemNumber - The item number from the token.
     * @param label - The button label.
     * @returns The button markup for an add to cart or add to template token.
     */
    private _buildButton(className: string, itemNumber: string, label: string): string {
        return (
            `<button type="button" class="${className}" ${ITEM_NUMBER_ATTRIBUTE}="${this._escapeHtml(itemNumber)}">` +
            `${this._escapeHtml(label)}</button>`
        );
    }

    /**
     * Escapes text for safe insertion into generated markup, including attribute values.
     * @param value - The raw text.
     * @returns The escaped text.
     */
    private _escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    /**
     * Single delegated click handler for every button the module generated.
     * @param event - The click event raised inside the promo content wrapper.
     */
    private readonly _handlePromoClick = (event: React.MouseEvent<HTMLDivElement>): void => {
        const button = (event.target as HTMLElement | null)?.closest<HTMLElement>(`.${ADD_TO_CART_CLASS}, .${ADD_TO_TEMPLATE_CLASS}`);

        if (!button || !event.currentTarget.contains(button)) {
            return;
        }

        const itemNumber = button.getAttribute(ITEM_NUMBER_ATTRIBUTE);

        if (!itemNumber || this._pendingItems.has(itemNumber)) {
            return;
        }

        event.preventDefault();

        const quantity = this._getQuantity(itemNumber);

        if (quantity === undefined) {
            this._showError();
            return;
        }

        const action: PromoAction = button.classList.contains(ADD_TO_TEMPLATE_CLASS) ? 'addToTemplate' : 'addToCart';

        void this._startAction({ action, itemNumber, quantity, button });
    };

    /**
     * Reads the quantity from the {quantity:ITEM} input for the same item. Both actions use it.
     * @param itemNumber - The item number from the clicked button.
     * @returns The quantity, 1 when the promo has no quantity token for the item, or undefined
     * when the entered value is not a usable quantity.
     */
    private _getQuantity(itemNumber: string): number | undefined {
        const input = this._contentRef.current?.querySelector<HTMLInputElement>(
            `input.${QUANTITY_CLASS}[${ITEM_NUMBER_ATTRIBUTE}="${this._escapeSelectorValue(itemNumber)}"]`
        );

        if (!input) {
            return 1;
        }

        const quantity = Number.parseInt(input.value, 10);

        return Number.isFinite(quantity) && quantity >= 1 ? quantity : undefined;
    }

    /**
     * Resolves the item, then refuses it, tells the customer it has been replaced, or runs it.
     * @param request - The action the customer asked for.
     */
    private async _startAction(request: IPromoActionRequest): Promise<void> {
        const { action, itemNumber, button } = request;

        if (action === 'addToTemplate') {
            const { user } = this.props.context.request;

            if (!user.isAuthenticated && user.signInUrl) {
                window.location.assign(user.signInUrl);
                return;
            }
        }

        this._beginAction(itemNumber, button);

        let product: SimpleProduct | undefined;

        try {
            product = await this._getProduct(itemNumber);
        } catch (error) {
            this.props.context.telemetry.error(`[gdn-promo-program] Lookup failed for item '${itemNumber}': ${String(error)}`);
        } finally {
            this._endAction(itemNumber, button);
        }

        if (!product) {
            this._showError();
            return;
        }

        // On sites that disable back orders, an item with no stock cannot be bought at all.
        if (this._isBackOrderRestricted(product)) {
            this.props.context.telemetry.warning(`[gdn-promo-program] Item '${itemNumber}' is out of stock and back orders are disabled.`);
            this._showError(this._unavailableMessage);
            return;
        }

        // Tell the customer about a replacement before adding it: a successful add opens the
        // mini cart, and the flag that opens it is a read-only observable on the shared cart
        // state, so a notice shown afterwards would land on top of it.
        if (this._isSuperseded(itemNumber, product)) {
            this._showSupersession({ ...request, product });
            return;
        }

        await this._runAction({ ...request, product });
    }

    /**
     * Runs a resolved action.
     * @param request - The resolved action.
     */
    private async _runAction(request: IResolvedPromoAction): Promise<void> {
        const { action, itemNumber, quantity, button, product } = request;

        this._beginAction(itemNumber, button);

        try {
            if (action === 'addToTemplate') {
                await this._addToOrderTemplate(product, quantity);
            } else {
                await this._addToCart(product, quantity, itemNumber);
            }
        } catch (error) {
            this.props.context.telemetry.error(`[gdn-promo-program] '${action}' failed for item '${itemNumber}': ${String(error)}`);
            this._showError();
        } finally {
            this._endAction(itemNumber, button);
        }
    }

    /**
     * Adds a resolved product to the cart. Success needs no message of its own: the standard
     * Commerce mini cart opens and reports the addition.
     * @param product - The resolved product.
     * @param quantity - The quantity from the promo content.
     * @param itemNumber - The item number from the promo content, for telemetry.
     */
    private async _addToCart(product: SimpleProduct, quantity: number, itemNumber: string): Promise<void> {
        const cart = await this.props.data.cart;

        if (!cart) {
            this._showError();
            return;
        }

        const result = await cart.addProductToCart({ product, count: quantity, catalogId: getCatalogId(this.props.context.request) });

        if (result.status !== 'SUCCESS') {
            this.props.context.telemetry.warning(`[gdn-promo-program] Add to cart returned '${result.status}' for item '${itemNumber}'.`);
            this._showError();
        }
    }

    /**
     * Hands a resolved product and the quantity from the promo content to the standard Commerce
     * order template dialog, which owns template selection, creation and duplicate handling.
     * @param product - The resolved product.
     * @param quantity - The quantity from the promo content.
     */
    private async _addToOrderTemplate(product: SimpleProduct, quantity: number): Promise<void> {
        // Order templates load on the client, so make sure they have arrived first.
        const orderTemplates = await this.props.data.orderTemplates;

        if (!orderTemplates) {
            this._showError();
            return;
        }

        this._orderTemplateRequestCount += 1;
        this.setState({ orderTemplateRequest: { product, quantity, id: this._orderTemplateRequestCount } });
    }

    /**
     * Renders the standard Commerce Add to Order Template component off-screen. It supplies
     * only the Commerce dialogs; the visible button is the one generated from the token.
     * @returns The hidden component, or null before the first add to template request.
     */
    private _renderAddToOrderTemplate(): React.ReactNode {
        const { orderTemplateRequest } = this.state;
        const {
            id,
            typeName,
            context,
            resources,
            data: { orderTemplates, customerInformation }
        } = this.props;

        if (!orderTemplateRequest || !orderTemplates.result) {
            return null;
        }

        const isMultipleCatalogFeatureEnabledInHq = this._isChannelMultipleCatalogsFeatureEnabled();
        const catalogId = getCatalogId(context.request);

        return (
            <div className='gdn-promo-program__order-template-host' ref={this._orderTemplateHostRef} aria-hidden='true'>
                <AddToOrderTemplateComponent
                    className='gdn-promo-program__order-template-trigger'
                    addToOrderTemplateButtonText={resources.addToOrderTemplateButtonText}
                    addToOrderTemplateButtonTooltip={resources.addToOrderTemplateButtonTooltip}
                    addToOrderTemplateDialogResources={{
                        addToOrderTemplateHeader: resources.addToOrderTemplateHeader,
                        noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
                        noOrderTemplatesDescription: resources.noOrderTemplatesDescription,
                        createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
                        createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
                        cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,
                        selectTemplatesText: resources.selectTemplatesText,
                        addToTemplateButtonText: resources.addToTemplateButtonText,
                        lineItemsText: resources.lineItemsText
                    }}
                    createOrderTemplateDialogResources={{
                        orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
                        orderTemplateTitleLabel: resources.orderTemplateTitle,
                        orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
                        orderTemplateDescription: resources.createOrderTemplateDescription,
                        defaultOrderTemplateName: resources.defaultOrderTemplateName,
                        orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
                        orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
                    }}
                    itemAddedToOrderTemplateDialogResources={{
                        viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
                        continueShoppingButtonText: resources.continueShoppingButtonText,
                        itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
                        itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
                        itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
                        freePriceText: resources.priceFree,
                        originalPriceText: resources.originalPriceText,
                        currentPriceText: resources.currentPriceText
                    }}
                    duplicateItemsWhenAddingToOrderTemplateDialogResources={{
                        duplicatedProductsHeader: resources.duplicatedProductsHeader,
                        duplicatedProductsDescription: resources.duplicatedProductsDescription,
                        updateQuantityButtonText: resources.updateQuantityButtonText,
                        cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText
                    }}
                    data={{
                        product: orderTemplateRequest.product,
                        quantity: orderTemplateRequest.quantity,
                        orderTemplates: orderTemplates.result,
                        customerInformation: customerInformation.result
                    }}
                    context={context}
                    id={id}
                    typeName={typeName}
                    onError={this._handleOrderTemplateError}
                    showButtonText={false}
                    showButtonTooltip={false}
                    showStatusMessage={false}
                    isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
                    {...(isMultipleCatalogFeatureEnabledInHq && { catalogId })}
                />
            </div>
        );
    }

    /**
     * Opens the Commerce order template dialog for a new request by clicking the off-screen
     * trigger, which is the only way the component exposes its dialogs.
     */
    private _openOrderTemplateDialogIfRequested(): void {
        const requestId = this.state.orderTemplateRequest?.id;

        if (requestId === undefined || requestId === this._openedOrderTemplateRequestId) {
            return;
        }

        this._openedOrderTemplateRequestId = requestId;

        const trigger = this._orderTemplateHostRef.current?.querySelector<HTMLButtonElement>(
            'button.gdn-promo-program__order-template-trigger'
        );

        if (!trigger) {
            // The component renders nothing outside a B2B channel, so there is no dialog to open.
            this.props.context.telemetry.warning('[gdn-promo-program] Add to Order Template is unavailable for this channel.');
            this._showError();
            return;
        }

        // The trigger is visually hidden and only ever activated from here.
        trigger.tabIndex = -1;
        trigger.click();
    }

    private readonly _handleOrderTemplateError = (): void => {
        this._showError();
    };

    /**
     * Resolves every item number in the tokens in the background, so the first click on any
     * button does not wait for a lookup. It batches, so the cost is the same few requests
     * whether the promotion lists ten items or two hundred.
     */
    private _prefetchProducts(): void {
        if (this._tokenItemNumbers.length > 0) {
            this._prefetch = this._resolveProducts(this._tokenItemNumbers);
        }
    }

    /**
     * Resolves an item number to a product, preferring the prefetched cache and falling back to
     * an on-demand lookup so a failed or incomplete prefetch still works.
     * @param itemNumber - The item number from the token.
     * @returns The resolved product, or undefined when the item does not resolve.
     */
    private async _getProduct(itemNumber: string): Promise<SimpleProduct | undefined> {
        if (this._prefetch) {
            await this._prefetch;
        }

        if (!this._productCache.has(itemNumber)) {
            await this._resolveProducts([itemNumber]);
        }

        return this._productCache.get(itemNumber) ?? undefined;
    }

    /**
     * Looks up a batch of item numbers and records the outcome for each one in the cache.
     *
     * An item that resolves to nothing is cached as null so repeated clicks do not re-issue a
     * request that is known to fail. A thrown request is deliberately not cached, so a transient
     * failure does not disable the item for the rest of the session.
     * @param itemNumbers - The item numbers to resolve.
     */
    private async _resolveProducts(itemNumbers: string[]): Promise<void> {
        const {
            actionContext,
            request,
            request: {
                apiSettings,
                apiSettings: { channelId }
            }
        } = this.props.context;

        const catalogId = getCatalogId(request);

        try {
            const searchCriteria: ProductSearchCriteria = {
                Context: { ChannelId: channelId, CatalogId: catalogId },
                IncludeAttributes: false,
                ItemIds: itemNumbers.map(itemNumber => ({ ItemId: itemNumber }))
            };

            const searchResults: ProductSearchResult[] = await searchByCriteriaAsync(
                // Top matches the Retail Server cap, as in get-simple-products.override.action.
                { callerContext: actionContext, queryResultSettings: { Paging: { Top: 1000 } } },
                searchCriteria
            );

            const recordIdsByItemNumber = new Map<string, number>();

            for (const itemNumber of itemNumbers) {
                const match = this._findSearchResult(searchResults, itemNumber);

                if (match?.RecordId) {
                    recordIdsByItemNumber.set(itemNumber, match.RecordId);
                } else {
                    this._productCache.set(itemNumber, null);
                    this.props.context.telemetry.warning(`[gdn-promo-program] Item '${itemNumber}' did not resolve to a product.`);
                }
            }

            if (recordIdsByItemNumber.size === 0) {
                return;
            }

            // Resolve the full products in one call so the cart and order template actions get
            // the record id, unit of measure and image they need.
            const products = await getSimpleProductsAction(
                Array.from(recordIdsByItemNumber.values()).map(
                    recordId => new ProductInput(recordId, apiSettings, channelId, undefined, request, catalogId)
                ),
                actionContext
            );

            // Match on record id rather than position, so the mapping does not depend on the
            // action returning products in the order they were requested.
            const productsByRecordId = new Map<number, SimpleProduct>();

            for (const product of products) {
                if (product?.RecordId) {
                    productsByRecordId.set(product.RecordId, product);
                }
            }

            if (this._doNotAllowBackOrder) {
                await this._loadAvailability(Array.from(productsByRecordId.keys()));
            }

            for (const [itemNumber, recordId] of recordIdsByItemNumber) {
                const product = productsByRecordId.get(recordId);

                if (product) {
                    this._productCache.set(itemNumber, product);
                } else {
                    this._productCache.set(itemNumber, null);
                    this.props.context.telemetry.warning(`[gdn-promo-program] Item '${itemNumber}' did not resolve to a full product.`);
                }
            }
        } catch (error) {
            this.props.context.telemetry.error(`[gdn-promo-program] Product lookup failed: ${String(error)}`);
        }
    }

    /**
     * Finds the search result for a requested item number.
     *
     * A superseded item does not come back under the number that was asked for: Commerce returns
     * the item it supersedes to, and keeps the requested number on ProductNumber and in the
     * superseded-from-id extension property. So an exact ItemId match is tried first, then those.
     * @param searchResults - The results of the batched item lookup.
     * @param itemNumber - The item number from the token.
     * @returns The matching result, or undefined when the item does not resolve.
     */
    private _findSearchResult(searchResults: ProductSearchResult[], itemNumber: string): ProductSearchResult | undefined {
        const exactMatch = searchResults.find(result => result.ItemId === itemNumber);

        if (exactMatch) {
            return exactMatch;
        }

        const supersededMatch = searchResults.find(
            result =>
                result.ProductNumber === itemNumber ||
                getExtensionPropertyValueByKey(
                    result.ExtensionProperties,
                    SupersessionKey.SupersededId,
                    CommercePropertyValueEnum.string
                ) === itemNumber
        );

        if (supersededMatch) {
            this.props.context.telemetry.information(
                `[gdn-promo-program] Item '${itemNumber}' is superseded by '${supersededMatch.ItemId ?? ''}'.`
            );
        }

        return supersededMatch;
    }

    /**
     * Loads inventory availability for the products just resolved. Only called on sites that
     * disable back orders, and batched with the product lookup, matching how the search results
     * page reads availability once per page.
     * @param recordIds - The product record ids to check.
     */
    private async _loadAvailability(recordIds: number[]): Promise<void> {
        const { actionContext } = this.props.context;

        try {
            const searchCriteria = createInventoryAvailabilitySearchCriteria(actionContext, recordIds, true);
            const response = await getEstimatedAvailabilityAsync({ callerContext: actionContext }, searchCriteria);

            for (const availability of response.ProductWarehouseInventoryAvailabilities ?? []) {
                if (availability.ProductId !== undefined) {
                    this._availabilityByRecordId.set(availability.ProductId, availability.TotalAvailableInventoryLevelCode ?? '');
                }
            }
        } catch (error) {
            // Availability is a restriction, not a requirement. If the lookup fails the add
            // proceeds and Commerce remains the authority, as it does elsewhere on the site.
            this.props.context.telemetry.error(`[gdn-promo-program] Availability lookup failed: ${String(error)}`);
        }
    }

    /**
     * @param product - The resolved product.
     * @returns Whether the item is out of stock on a site that disallows back orders.
     */
    private _isBackOrderRestricted(product: SimpleProduct): boolean {
        return this._doNotAllowBackOrder && this._availabilityByRecordId.get(product.RecordId) === BACK_ORDER_LEVEL_CODE;
    }

    /**
     * @returns Whether this site disables back orders, set in Site Builder extensions.
     */
    private get _doNotAllowBackOrder(): boolean {
        return Boolean(this.props.context.app.config.doNotAllowBackOrder);
    }

    /**
     * @param itemNumber - The item number from the token.
     * @param product - The product that item number resolved to.
     * @returns Whether Commerce returned a different item than the one that was asked for.
     */
    private _isSuperseded(itemNumber: string, product: SimpleProduct): boolean {
        const resolved = (product.ItemId ?? '').trim();

        return resolved.length > 0 && resolved !== itemNumber;
    }

    /**
     * Shows the failure dialog. There is no success counterpart: a successful add opens the
     * standard Commerce mini cart, which already tells the customer what happened.
     * @param message - A specific message, when the generic one is not accurate enough.
     */
    private _showError(message?: string): void {
        const { config, resources } = this.props;
        const labels = config.promoErrorModalLabels;

        // An error never carries on with an action, so drop any that was waiting.
        this._pendingAction = undefined;

        this.setState({
            modal: {
                className: 'gdn-promo-modal--error',
                heading: labels?.errorModalHeadingLabel ?? resources.errorModalHeadingFallbackLabel,
                message: message ?? labels?.errorModalMessageLabel ?? resources.errorModalMessageFallbackLabel,
                okButtonLabel: labels?.okButtonLabel ?? resources.okButtonLabel
            }
        });
    }

    /**
     * Tells the customer their item has been replaced. Dismissing the dialog adds the replacement.
     * @param request - The resolved action for the replacement item.
     */
    private _showSupersession(request: IResolvedPromoAction): void {
        const { config, resources } = this.props;
        const labels = config.promoSupersessionModalLabels;
        const template = labels?.supersessionModalMessageLabel ?? resources.supersessionModalMessageFallbackLabel;

        this._pendingAction = request;

        this.setState({
            modal: {
                className: 'gdn-promo-modal--supersession',
                heading: labels?.supersessionModalHeadingLabel ?? resources.supersessionModalHeadingFallbackLabel,
                message: template.replace('{0}', request.itemNumber).replace('{1}', request.product.ItemId ?? ''),
                detail: request.product.Name,
                okButtonLabel: labels?.okButtonLabel ?? resources.okButtonLabel
            }
        });
    }

    /**
     * Closes the dialog, then carries on with the action a supersession notice was holding.
     */
    private readonly _closeModal = (): void => {
        const pending = this._pendingAction;

        this._pendingAction = undefined;
        this.setState({ modal: undefined });

        if (pending) {
            void this._runAction(pending);
        }
    };

    /**
     * @returns The message for an item that is out of stock and cannot be back ordered.
     */
    private get _unavailableMessage(): string {
        return this.props.config.promoErrorModalLabels?.unavailableMessageLabel ?? this.props.resources.unavailableMessageFallbackLabel;
    }

    /**
     * Marks an action as in flight so repeated clicks are ignored while it runs.
     * @param itemNumber - The item the action is running for.
     * @param button - The button to put into its loading state.
     */
    private _beginAction(itemNumber: string, button: HTMLElement): void {
        this._pendingItems.add(itemNumber);
        this._setButtonLoading(button, true);
    }

    /**
     * Releases an in-flight action.
     * @param itemNumber - The item the action ran for.
     * @param button - The button to take out of its loading state.
     */
    private _endAction(itemNumber: string, button: HTMLElement): void {
        this._pendingItems.delete(itemNumber);
        this._setButtonLoading(button, false);
    }

    /**
     * @param element - The button element.
     * @param loading - Whether the action is in flight.
     */
    private _setButtonLoading(element: HTMLElement, loading: boolean): void {
        if (element instanceof HTMLButtonElement) {
            element.disabled = loading;
        }

        element.setAttribute('aria-busy', loading ? 'true' : 'false');
        element.classList.toggle(LOADING_CLASS, loading);
    }

    /**
     * Escapes a value for use inside an attribute selector. Item numbers contain spaces and
     * dashes, so they cannot be interpolated unescaped.
     * @param value - The raw attribute value.
     * @returns The escaped value.
     */
    private _escapeSelectorValue(value: string): string {
        if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
            return CSS.escape(value);
        }

        return value.replace(/["\\]/g, '\\$&');
    }

    /**
     * @returns Whether the channel multiple catalogs feature is enabled in HQ.
     */
    private _isChannelMultipleCatalogsFeatureEnabled(): boolean {
        return (
            this.props.data.featureState?.result?.find(feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature')
                ?.IsEnabled || false
        );
    }
}

export default GdnPromoProgram;
